import type { NotificationData } from "@qendo/notifications";
import { router } from "expo-router";
import { useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";
import { Button } from "~/components/Button";
import {
    DropdownContent,
    DropdownItem,
    DropdownItemIcon,
    DropdownItemTitle,
    DropdownRoot,
    DropdownTrigger,
} from "~/components/dropdown";
import { Paragraphs, Subtitle, Title } from "~/components/layout/text";
import { QendoIcon } from "~/components/QendoIcon";
import { Radius, Space } from "~/constants/sizing";
import { useNotifications } from "~/providers/NotificationsProvider";
import { useTheme } from "~/providers/ThemeProvider";
import { formatFuzzyTimeForHumans } from "~/util/dates";
import { setWidthOnLayout, titleWidth } from "~/util/layout";

export type NotificationListingItemProps = {
    item: NotificationData;
};

const ICON_SIZE = 24;

export const NotificationListingItem = ({ item }: NotificationListingItemProps) => {
    const { border, background, colours } = useTheme();
    const { markSeen, markUnSeen, dismiss } = useNotifications();
    const [width, setWidth] = useState(300);
    const onLayout = setWidthOnLayout(setWidth);
    const { center } = titleWidth(width, Space.sm, ICON_SIZE, ICON_SIZE);
    const { identifier, seen, date, href, title, subtitle, body } = item;

    const handlePress = () => {
        if (!seen) {
            markSeen(identifier);
        }
        if (href) {
            router.navigate(href);
        }
    };

    return (
        <Pressable
            style={[styles.notification, background.primary, seen ? styles.seen : border.selected]}
            onPress={handlePress}
        >
            <QendoIcon
                name={item.icon ?? "heart"}
                size={ICON_SIZE}
                color={colours.icon.secondary}
                style={styles.icon}
            />
            <View style={[styles.body, { width: center }]} onLayout={onLayout}>
                <View style={styles.content}>
                    {title && <Title size="base">{title}</Title>}
                    {subtitle && <Subtitle size="sm">{subtitle}</Subtitle>}
                    {body && <Paragraphs content={body} />}
                </View>
                <Subtitle>{formatFuzzyTimeForHumans(date, true)}</Subtitle>
            </View>
            <DropdownRoot>
                <DropdownTrigger>
                    <Button
                        icon="ellipsis"
                        variant="neutral-quiet"
                        size="md"
                        iconSize={ICON_SIZE}
                        style={styles.icon}
                    />
                </DropdownTrigger>
                <DropdownContent>
                    {seen ? (
                        <DropdownItem key="mark-unseen" onSelect={() => markUnSeen(identifier)}>
                            <DropdownItemTitle>Mark unseen</DropdownItemTitle>
                            <DropdownItemIcon icon="email" ios={{ name: "envelope" }} />
                        </DropdownItem>
                    ) : (
                        <DropdownItem key="mark-seen" onSelect={() => markSeen(identifier)}>
                            <DropdownItemTitle>Mark seen</DropdownItemTitle>
                            <DropdownItemIcon icon="calendar" ios={{ name: "envelope.open" }} />
                        </DropdownItem>
                    )}
                    <DropdownItem key="dismiss" onSelect={() => dismiss(identifier)} destructive>
                        <DropdownItemTitle>Delete this notification</DropdownItemTitle>
                        <DropdownItemIcon icon="list" ios={{ name: "list.bullet" }} />
                    </DropdownItem>
                </DropdownContent>
            </DropdownRoot>
        </Pressable>
    );
};

const styles = StyleSheet.create({
    notification: {
        paddingHorizontal: Space.md,
        paddingVertical: Space.sm,
        gap: Space.sm,
        flexDirection: "row",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        borderWidth: 1,
        borderRadius: Radius.lg,
    },
    seen: {
        borderColor: "transparent",
    },
    icon: {
        flexShrink: 0,
        width: ICON_SIZE,
    },
    body: {
        flexGrow: 1,
        gap: Space.sm,
        alignItems: "stretch",
    },
    content: {
        gap: Space.xxs,
        alignItems: "stretch",
    },
});
